import { useCallback, useEffect, useRef, useState } from "react";

export type VoiceRecorderStatus =
  | "idle"
  | "requesting-permission"
  | "recording"
  | "stopping"
  | "error";

export interface VoiceRecordingResult {
  blob: Blob;
  mimeType: string;
  audioDurationMs: number;
  wallClockMs: number;
  audioBytes: number;
}

interface WakeLockSentinelLike {
  release: () => Promise<void>;
}

interface UseVoiceRecorderOptions {
  silenceGating?: boolean;
  silenceThreshold?: number;
  pauseAfterMs?: number;
}

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/wav",
];

const MIN_RECORDING_MS = 500;
export const MAX_VOICE_RECORDING_MS = 5 * 60 * 1000;
export const MAX_VOICE_WALL_CLOCK_MS = 15 * 60 * 1000;
const DEFAULT_SILENCE_THRESHOLD = 0.012;
const DEFAULT_PAUSE_AFTER_MS = 2800;

/**
 * Number of bars held in the rolling waveform buffer. The recording strip scrolls
 * right-to-left: the newest amplitude is appended on the right and the oldest drops
 * off the left. Exported so the waveform component renders exactly this many slots.
 */
export const WAVEFORM_BAR_COUNT = 512;
/** Cadence at which a new bar is pushed to the waveform buffer (≈18 bars/sec → faster scroll). */
const LEVEL_SAMPLE_INTERVAL_MS = 55;

function createSilentLevels(): number[] {
  return new Array<number>(WAVEFORM_BAR_COUNT).fill(0);
}

export function getPreferredVoiceMimeType(): string {
  if (typeof MediaRecorder === "undefined") {
    return "";
  }

  return MIME_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? "";
}

export function formatRecordingTime(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function useVoiceRecorder({
  silenceGating = true,
  silenceThreshold = DEFAULT_SILENCE_THRESHOLD,
  pauseAfterMs = DEFAULT_PAUSE_AFTER_MS,
}: UseVoiceRecorderOptions = {}) {
  const [status, setStatus] = useState<VoiceRecorderStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [wallClockMs, setWallClockMs] = useState(0);
  const [isPausedForSilence, setIsPausedForSilence] = useState(false);
  const [level, setLevel] = useState(0);
  const [levels, setLevels] = useState<number[]>(createSilentLevels);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef(0);
  const activeStartedAtRef = useRef(0);
  const accumulatedRecordingMsRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const analyserFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);
  const hasSpeechRef = useRef(false);
  const silentSinceRef = useRef<number | null>(null);
  const lastSampleAtRef = useRef(0);

  const cleanup = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (analyserFrameRef.current !== null) {
      window.cancelAnimationFrame(analyserFrameRef.current);
      analyserFrameRef.current = null;
    }
    void audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
    void wakeLockRef.current?.release().catch(() => {});
    wakeLockRef.current = null;
    setIsPausedForSilence(false);
    setLevel(0);
    setLevels(createSilentLevels());
  }, []);

  const tick = useCallback(() => {
    const now = performance.now();
    setWallClockMs(now - startedAtRef.current);
    const activeElapsed =
      recorderRef.current?.state === "recording" ? now - activeStartedAtRef.current : 0;
    setElapsedMs(accumulatedRecordingMsRef.current + activeElapsed);
  }, []);

  const pauseForSilence = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    accumulatedRecordingMsRef.current += performance.now() - activeStartedAtRef.current;
    recorder.pause();
    setIsPausedForSilence(true);
    tick();
  }, [tick]);

  const resumeFromSilence = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    activeStartedAtRef.current = performance.now();
    recorder.resume();
    setIsPausedForSilence(false);
    tick();
  }, [tick]);

  const startSilenceMonitor = useCallback(
    (stream: MediaStream) => {
      if (!silenceGating || typeof AudioContext === "undefined") {
        return;
      }

      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = audioContext;
      const samples = new Uint8Array(analyser.fftSize);

      const analyze = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) {
          const normalized = (sample - 128) / 128;
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / samples.length);
        const normalizedLevel = Math.min(1, rms * 8);

        const now = performance.now();
        // Silence detection runs every frame so resume latency stays ~tens of ms.
        if (rms >= silenceThreshold) {
          hasSpeechRef.current = true;
          silentSinceRef.current = null;
          resumeFromSilence();
        } else if (hasSpeechRef.current) {
          silentSinceRef.current ??= now;
          if (now - silentSinceRef.current >= pauseAfterMs) {
            pauseForSilence();
          }
        }

        // Visual buffer updates on a fixed clock so the scroll speed is frame-rate
        // independent. While paused for silence the waveform freezes — stop pushing
        // so the frozen tail (already quiet) reads as baseline dots, not new bars.
        const isPaused = recorderRef.current?.state === "paused";
        if (!isPaused && now - lastSampleAtRef.current >= LEVEL_SAMPLE_INTERVAL_MS) {
          lastSampleAtRef.current = now;
          setLevel(normalizedLevel);
          setLevels((prev) => {
            const next = prev.slice(1);
            next.push(normalizedLevel);
            return next;
          });
        }

        analyserFrameRef.current = window.requestAnimationFrame(analyze);
      };

      analyserFrameRef.current = window.requestAnimationFrame(analyze);
    },
    [pauseAfterMs, pauseForSilence, resumeFromSilence, silenceGating, silenceThreshold]
  );

  const start = useCallback(async () => {
    if (status === "recording" || status === "requesting-permission" || status === "stopping") {
      return;
    }

    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus("error");
      setError("Microphone recording is not supported in this browser.");
      return;
    }

    setStatus("requesting-permission");
    setError(null);
    setElapsedMs(0);
    setWallClockMs(0);
    chunksRef.current = [];
    accumulatedRecordingMsRef.current = 0;
    hasSpeechRef.current = false;
    silentSinceRef.current = null;
    lastSampleAtRef.current = 0;
    setLevels(createSilentLevels());

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      const mimeType = getPreferredVoiceMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      streamRef.current = stream;
      recorderRef.current = recorder;
      startedAtRef.current = performance.now();
      activeStartedAtRef.current = startedAtRef.current;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onerror = () => {
        setError("Recording failed. Please try again.");
        setStatus("error");
        cleanup();
      };

      const wakeLock = (
        navigator as Navigator & {
          wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
        }
      ).wakeLock;
      wakeLockRef.current = wakeLock ? await wakeLock.request("screen").catch(() => null) : null;

      recorder.start(250);
      setStatus("recording");
      startSilenceMonitor(stream);
      timerRef.current = window.setInterval(tick, 250);
      tick();
    } catch (cause: unknown) {
      cleanup();
      setStatus("error");
      setError(cause instanceof Error ? cause.message : "Microphone permission was denied.");
    }
  }, [cleanup, startSilenceMonitor, status, tick]);

  const stop = useCallback(async (): Promise<VoiceRecordingResult | null> => {
    const recorder = recorderRef.current;
    if (!recorder || status !== "recording") {
      return null;
    }

    setStatus("stopping");
    if (recorder.state === "recording") {
      accumulatedRecordingMsRef.current += performance.now() - activeStartedAtRef.current;
    }

    const finalWallClockMs = performance.now() - startedAtRef.current;
    const finalAudioDurationMs = accumulatedRecordingMsRef.current;

    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    await stopped;

    const blob = new Blob(chunksRef.current, {
      type: recorder.mimeType || getPreferredVoiceMimeType() || "audio/webm",
    });
    cleanup();
    recorderRef.current = null;
    chunksRef.current = [];
    setStatus("idle");
    setElapsedMs(0);
    setWallClockMs(0);

    if (finalAudioDurationMs < MIN_RECORDING_MS || blob.size === 0) {
      return null;
    }

    if (
      finalAudioDurationMs > MAX_VOICE_RECORDING_MS ||
      finalWallClockMs > MAX_VOICE_WALL_CLOCK_MS
    ) {
      setError("Recording is too long. Please try a shorter recording.");
      setStatus("error");
      return null;
    }

    return {
      blob,
      mimeType: blob.type || "audio/webm",
      audioDurationMs: Math.round(finalAudioDurationMs),
      wallClockMs: Math.round(finalWallClockMs),
      audioBytes: blob.size,
    };
  }, [cleanup, status]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];
    cleanup();
    setStatus("idle");
    setError(null);
    setElapsedMs(0);
    setWallClockMs(0);
  }, [cleanup]);

  useEffect(() => cleanup, [cleanup]);

  return {
    status,
    error,
    elapsedMs,
    wallClockMs,
    isRecording: status === "recording",
    isStopping: status === "stopping",
    isPausedForSilence,
    level,
    levels,
    start,
    stop,
    cancel,
  };
}
